import type { MantineRadius } from '@mantine/core';
import { flagSrc, logoSrc } from '~portal/lib/logo-flags';
import { EntityLogo as EntityTile, type EntityLogoProps as EntityTileProps } from '~ui';

export type EntityLogoProps = {
  name: string;
  /** @desc Tile edge in px. Figma draws listing tiles at 40. */
  size?: number;
  /** @desc Corner radius; `md` (8px) for the 40px tile, `lg` (16px) for the 96px masthead tile (C3). */
  radius?: MantineRadius;
  /** @desc A real logo URL, when the caller already has one (a CMS image, say). */
  src?: string | null;
  /** @desc A brand or group slug, resolved to `/logos/<slug>.svg` when such a logo is shipped. */
  logoSlug?: string;
  /**
   * @desc A flag code: an ISO country (`gb`), a `flag-icons` subdivision (`gb-eng`) or a US
   * state as `us-<postal>` (`us-ny`). See `lib/logo-flags.ts`; an unknown code shows the letters.
   */
  flagCode?: string;
  /** @desc Override the letter(s) shown, e.g. a market code (`GB`) instead of the initial. */
  code?: string;
};

/**
 * @desc Resolves a brand slug and a flag code to the asset URLs the shared tile takes. The assets
 * are portal build concerns — the `flag-icons` glob and `public/flags/us/*.webp` — so resolution
 * stays here while the tile itself lives in `internal/ui`.
 */
export const resolveEntityLogo = ({
  src,
  logoSlug,
  flagCode,
  ...tile
}: EntityLogoProps): EntityTileProps => ({
  ...tile,
  src: src ?? logoSrc(logoSlug),
  flagSrc: flagSrc(flagCode),
});

/**
 * @desc A company's or market's tile (decision D-8): the shared `internal/ui` tile, with a brand
 * slug or flag code resolved to the portal's own assets.
 */
export function EntityLogo(props: EntityLogoProps) {
  return <EntityTile {...resolveEntityLogo(props)} />;
}
